import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    School as SchoolIcon,
    LocationOn as LocationIcon,
    People as PeopleIcon,
    Person as PersonIcon,
    Business as BuildingIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    CalendarToday as CalendarIcon,
    TrendingUp as TrendingIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { liveAnalyticsAPI } from '../../services/liveAnalyticsApi';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';

const COLORS = ['#0088FE', '#FF8042'];

const SchoolDetail: React.FC = () => {
    const { schoolId } = useParams<{ schoolId: string }>();
    const navigate = useNavigate();

    const { data: schoolResponse, isLoading, error } = useQuery({
        queryKey: ['schoolDetails', schoolId],
        queryFn: () => liveAnalyticsAPI.getSchoolDetails(schoolId!),
        enabled: !!schoolId,
    });

    const school = schoolResponse?.data?.data;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !school) {
        return (
            <Box>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/schools')} sx={{ mb: 2 }}>
                    Back to Schools
                </Button>
                <Alert severity="error">
                    {error instanceof Error ? error.message : 'School not found'}
                </Alert>
            </Box>
        );
    }

    const currentEnrollment = school.enrollment?.current;
    const enrollmentHistory = school.enrollment?.history || [];
    const enrollmentByGrade = school.enrollment?.by_grade || [];

    // Prepare gender distribution data for pie chart
    const genderData = currentEnrollment
        ? [
            { name: 'Male', value: currentEnrollment.male_students },
            { name: 'Female', value: currentEnrollment.female_students },
        ]
        : [];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate('/schools')} sx={{ mr: 2 }}>
                    <BackIcon />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" gutterBottom>
                        {school.school_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={`EMIS ID: ${school.school_reg_number}`} size="small" />
                        <Chip
                            label={school.status === 1 ? 'Active' : 'Inactive'}
                            color={school.status === 1 ? 'success' : 'default'}
                            size="small"
                        />
                        <Chip label={school.school_type} color="info" variant="outlined" size="small" />
                        {school.education_level_name && (
                            <Chip label={school.education_level_name} color="secondary" variant="outlined" size="small" />
                        )}
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Overview Cards */}
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Students</Typography>
                            </Box>
                            <Typography variant="h3" color="primary">
                                {currentEnrollment?.total_students || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Current Enrollment
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PersonIcon color="secondary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Teachers</Typography>
                            </Box>
                            <Typography variant="h3" color="secondary">
                                {school.staff?.total_staff || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Staff
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingIcon color="success" sx={{ mr: 1 }} />
                                <Typography variant="h6">Ratio</Typography>
                            </Box>
                            <Typography variant="h3" color="success.main">
                                {school.student_teacher_ratio || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Students per Teacher
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <BuildingIcon color="info" sx={{ mr: 1 }} />
                                <Typography variant="h6">Classes</Typography>
                            </Box>
                            <Typography variant="h3" color="info.main">
                                {school.classes_summary?.total_classes || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {school.classes_summary?.total_streams || 0} streams
                                {school.infrastructure?.classrooms > 0 && ` | ${school.infrastructure.classrooms} rooms`}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* School Information */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <SchoolIcon sx={{ mr: 1 }} />
                            School Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Establishment Year
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {school.establishment_year || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Registration Date
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {school.registration_date
                                        ? new Date(school.registration_date).toLocaleDateString()
                                        : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <PhoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                    Phone
                                </Typography>
                                <Typography variant="body1">{school.contact?.phone || 'Not available'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                    Email
                                </Typography>
                                <Typography variant="body1">{school.contact?.email || 'Not available'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Postal Address
                                </Typography>
                                <Typography variant="body1">
                                    {school.contact?.postal_address || 'Not available'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Location */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationIcon sx={{ mr: 1 }} />
                            Location
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Region
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {school.location?.region?.region_name || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Code: {school.location?.region?.region_code || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    District
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {school.location?.district?.district_name || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Code: {school.location?.district?.district_code || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Shehia
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {school.location?.shehia?.shehia_name || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Code: {school.location?.shehia?.shehia_code || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Ward
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {school.location?.ward?.ward_name || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Code: {school.location?.ward?.ward_code || 'N/A'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Enrollment Trends */}
                {enrollmentHistory.length > 0 && (
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Enrollment Trends
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={enrollmentHistory}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="year" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="total_students" stroke="#8884d8" name="Total" />
                                    <Line type="monotone" dataKey="male_students" stroke="#0088FE" name="Male" />
                                    <Line type="monotone" dataKey="female_students" stroke="#FF8042" name="Female" />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>
                )}

                {/* Gender Distribution */}
                {genderData.length > 0 && (
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Gender Distribution
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={genderData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {genderData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Male: {currentEnrollment?.male_students || 0} | Female:{' '}
                                    {currentEnrollment?.female_students || 0}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                )}

                {/* Enrollment by Grade with Streams */}
                {enrollmentByGrade.length > 0 && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Enrollment by Grade
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={enrollmentByGrade}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="grade" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="male" fill="#0088FE" name="Male" />
                                    <Bar dataKey="female" fill="#FF8042" name="Female" />
                                </BarChart>
                            </ResponsiveContainer>

                            {/* Grade details table with streams */}
                            <TableContainer sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Grade</TableCell>
                                            <TableCell align="center">Streams</TableCell>
                                            <TableCell align="center">Male</TableCell>
                                            <TableCell align="center">Female</TableCell>
                                            <TableCell align="center">Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {enrollmentByGrade.map((grade: any, idx: number) => (
                                            <TableRow key={idx}>
                                                <TableCell>{grade.grade}</TableCell>
                                                <TableCell align="center">{grade.streams || 0}</TableCell>
                                                <TableCell align="center">{grade.male}</TableCell>
                                                <TableCell align="center">{grade.female}</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                    {grade.male + grade.female}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                {enrollmentByGrade.reduce((sum: number, g: any) => sum + (g.streams || 0), 0)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                {enrollmentByGrade.reduce((sum: number, g: any) => sum + g.male, 0)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                {enrollmentByGrade.reduce((sum: number, g: any) => sum + g.female, 0)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                {enrollmentByGrade.reduce((sum: number, g: any) => sum + g.male + g.female, 0)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                )}

                {/* Infrastructure */}
                {school.infrastructure && (
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Infrastructure
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Classrooms in Use
                                    </Typography>
                                    <Typography variant="h6">{school.infrastructure.classrooms}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Toilets (M / F)
                                    </Typography>
                                    <Typography variant="h6">
                                        {school.infrastructure.toilets_male} / {school.infrastructure.toilets_female}
                                    </Typography>
                                    {school.infrastructure.toilets_disability > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            + {school.infrastructure.toilets_disability} disability
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>
                                {[
                                    { key: 'has_library', label: 'Library' },
                                    { key: 'has_computer_lab', label: 'Science Lab' },
                                    { key: 'has_electricity', label: 'Electricity' },
                                    { key: 'has_water', label: 'Water Access' },
                                    { key: 'has_playground', label: 'Playground' },
                                    { key: 'has_ramps', label: 'Disability Ramps' },
                                ].map(({ key, label }) => (
                                    <Grid item xs={6} key={key}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {school.infrastructure[key] ? (
                                                <CheckIcon color="success" fontSize="small" />
                                            ) : (
                                                <CancelIcon color="error" fontSize="small" />
                                            )}
                                            <Typography variant="body2" sx={{ ml: 1 }}>
                                                {label}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>
                )}

                {/* Staff Information */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Staff Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Staff
                                </Typography>
                                <Typography variant="h6">{school.staff?.total_staff || 0}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Male
                                </Typography>
                                <Typography variant="h6">{school.staff?.male_staff || 0}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Female
                                </Typography>
                                <Typography variant="h6">{school.staff?.female_staff || 0}</Typography>
                            </Grid>
                            {school.student_teacher_ratio && (
                                <>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            Student-Teacher Ratio
                                        </Typography>
                                        <Typography variant="h5" color="primary">
                                            {school.student_teacher_ratio}:1
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Students per teacher
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SchoolDetail;
